// =====================================================================
// src/components/intl-acca/TierLockedTeaser.tsx
// =====================================================================
// Paywalled-teaser per PRD §3.2 — "Free tier teaser must show enough to
// create desire — not enough to remove the need to upgrade."
//
// Wraps the underlying content in a blur, overlays the upgrade CTA.
// Hosts pass `tierMeets(currentTier, requiredTier)` from
// src/auth/tier-gates.ts to decide whether to render this or unwrap.

import type { ReactNode } from "react";

export type IntlAccaGate =
  | "tournament_hub"
  | "focus_acca"
  | "value_acca"
  | "power_acca"
  | "dna_profiles"
  | "qualifier_intel"
  | "debrief_full"
  | "slip_archive";

interface TierLockedTeaserProps {
  /** What feature is locked — names the tier in the overlay copy. */
  gate: IntlAccaGate;
  /** The (visually-blurred) underlying content that hints at value. */
  children: ReactNode;
  /** Click handler — host wires this to a Paystack checkout / pricing route. */
  onUpgrade?: () => void;
}

const GATE_COPY: Record<IntlAccaGate, { tierName: string; title: string; body: string }> = {
  tournament_hub: {
    tierName: "Starter+",
    title: "Tournament Hub is Starter-tier.",
    body: "Standings, fixtures, and DNA roster activate from Starter and above.",
  },
  focus_acca: {
    tierName: "Sika Pro",
    title: "Focus Acca is Sika Pro.",
    body: "The 3-leg slip with full reasoning unlocks at Sika Pro and above.",
  },
  value_acca: {
    tierName: "Sika Pro",
    title: "Value Acca is Sika Pro.",
    body: "The 5-leg slip with edge breakdown unlocks at Sika Pro and above.",
  },
  power_acca: {
    tierName: "Pro",
    title: "Power Acca is Pro-tier.",
    body: "The 10-leg high-variance slip unlocks at Pro and above.",
  },
  dna_profiles: {
    tierName: "Pro",
    title: "DNA Profiles are Pro-tier.",
    body: "Manager, venue, and rivalry DNA per nation unlocks at Pro and above.",
  },
  qualifier_intel: {
    tierName: "Elite",
    title: "Qualifier Intel is Elite.",
    body: "Pre-tournament signals across CAF / UEFA windows are Elite-only.",
  },
  debrief_full: {
    tierName: "Sika Pro",
    title: "Full Debrief is Sika Pro.",
    body: "The leg-level CLV audit table unlocks at Sika Pro. Free users see the executive summary only.",
  },
  slip_archive: {
    tierName: "Sika Pro",
    title: "Slip Archive is Sika Pro.",
    body: "Every settled slip with leg-level CLV — the proof of process — unlocks at Sika Pro and above.",
  },
};

export function TierLockedTeaser({
  gate,
  children,
  onUpgrade,
}: TierLockedTeaserProps): JSX.Element {
  const copy = GATE_COPY[gate];
  return (
    <div className="ia-locked" aria-label={`${copy.title} Locked.`}>
      <div className="ia-locked-body" aria-hidden>
        {children}
      </div>
      <div className="ia-locked-overlay">
        <div className="ia-eyebrow">★ {copy.tierName.toUpperCase()} REQUIRED</div>
        <h3 className="ia-title" style={{ fontSize: "var(--sz-head-sm)" }}>
          {copy.title}
        </h3>
        <p className="ia-sub" style={{ textAlign: "center", maxWidth: 360 }}>
          {copy.body}
        </p>
        {onUpgrade ? (
          <button
            type="button"
            onClick={onUpgrade}
            style={{
              marginTop: 8,
              background: "var(--accent)",
              color: "var(--sz-text-inverse)",
              border: "none",
              borderRadius: "var(--sz-radius-md)",
              padding: "12px 22px",
              fontFamily: "var(--sz-font-mono)",
              fontSize: 11,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Upgrade → {copy.tierName}
          </button>
        ) : null}
      </div>
    </div>
  );
}
